import { useState, useRef } from "react";
import axios from "axios";
import { Upload, Loader2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const accessLevels = [
  { value: "everyone", label: "Everyone" },
  { value: "usergroup_a", label: "Usergroup A" },
  { value: "usergroup_b", label: "Usergroup B" },
  { value: "usergroup_c", label: "Usergroup C" },
];

export default function KbUploadDialog({ open, onOpenChange, subHub, folders, selectedFolder, onSuccess }) {
  const [folder, setFolder] = useState(selectedFolder || (folders?.[0] || ""));
  const [documentName, setDocumentName] = useState("");
  const [accessLevel, setAccessLevel] = useState("everyone");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [generatedId, setGeneratedId] = useState("");
  const fileInputRef = useRef(null);

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select a file");
      return;
    }
    if (!documentName.trim()) {
      toast.error("Please enter a document name");
      return;
    }

    setUploading(true);
    setGeneratedId("");

    const formData = new FormData();
    formData.append("file", file);
    formData.append("sub_hub", subHub);
    formData.append("folder", folder);
    formData.append("document_name", documentName.trim());
    formData.append("access_level", accessLevel);

    try {
      const res = await axios.post(`${API}/knowledge/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setGeneratedId(res.data.unique_id);
      toast.success(`Uploaded! ID: ${res.data.unique_id}`);
      setFile(null);
      setDocumentName("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-xl">Upload Document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Folder */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Folder</label>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger className="bg-slate-50 border-slate-300 rounded-none">
                <SelectValue placeholder="Select folder" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                {folders?.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Document Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Document Name</label>
            <Input
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Enter document name"
              className="bg-slate-50 border-slate-300 rounded-none"
            />
          </div>

          {/* Access Level */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Access Level</label>
            <Select value={accessLevel} onValueChange={setAccessLevel}>
              <SelectTrigger className="bg-slate-50 border-slate-300 rounded-none">
                <SelectValue placeholder="Select access level" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                {accessLevels.map((al) => (
                  <SelectItem key={al.value} value={al.value}>{al.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">File</label>
            <div
              className="border-2 border-dashed border-slate-300 p-6 text-center cursor-pointer hover:border-slate-400"
              onClick={() => fileInputRef.current?.click()}
            >
              {file ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-5 h-5 text-slate-500" />
                  <span className="text-sm text-slate-600">{file.name}</span>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Click to select a file</p>
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              className="hidden"
            />
          </div>

          {/* Generated ID */}
          {generatedId && (
            <div className="bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs text-slate-500 mb-1">Generated Unique ID:</p>
              <p className="text-sm font-mono font-bold text-slate-800">{generatedId}</p>
            </div>
          )}

          {/* Submit */}
          <Button
            onClick={handleSubmit}
            disabled={uploading}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-none"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" /> Upload Document</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
